/**
 * @category LeftSide
 * @component
 * @module Files/FileUploader
 * @description This component is part of the LeftSide and it is reponsible for file uploading and displaying
 * uploaded files.
 */
import React, { useRef, useContext, useState, useEffect } from "react";
import { DashboardContext } from "../../../context/DashboardContext";
import { GraphContext } from "../../../context/GraphContext";
import { AuthContext } from "../../../context/AuthContext";
import { FileHandlingContext } from "../../../context/FileHandlingContext";
import { GeneralContext } from "../../../context/GeneralContext";
import { WebSocketContext } from "../../../context/WebSocketContext";
import {
  useTutorialProgress,
  useTutorial,
} from "../../../context/TutorialContext";
import BlockingOverlay from "../../commonComponents/BlockingOverlay";
// import Button from "@mui/material/Button";
import deleteIcon from "../../../res/icons/delete.png";
import {
  //adjustModalPositionAndSize,
  downloadStringTxtFile,
  //generateWarningObject,
  hasProperty,
  isDeepEqual,
} from "../../../utils/helpers";
import "../leftSide.scss";
import HtmlTooltip from "../../commonComponents/HtmlTooltip";
import { deepCopy } from "../Models/modelLogic";
import Modal from "react-modal";
import FileProcessingWindow from "./fileProcessing/FileProcessingWindow";
//import SettingsIcon from "@mui/icons-material/Settings";
//import PresetLoader from "./fileProcessing/PresetLoader";
//import { processFileWithOptions } from "./fileProcessing/processingLogic";
//import { readFile } from "./fileProcessing/processingLogic";
import DownloadIcon from "@mui/icons-material/Download";
import { generatePlotData } from "../../middle/graphLogic";
import CustomButton from "../../commonComponents/CustomButton";

const FileUploader = () => {
  // Legacy file input refs - kept for reference
  // const fileInputRef = useRef(null);
  // const folderInputRef = useRef(null);
  const presetOptionRef = useRef(null);
  const {
    uploadedFiles,
    setUploadedFiles,
    modelData,
    setModelData,
    parametersOpen,
    fitOpen,
    maxIteration,
    iterationRefreshCount,
    maxRunTime,
    chiVal,
    //fileID,
    //setFileID,
    //setWarnings,
    //setNewWarningCount,
    //filePresetsLoadingOptions,
    //setfilePresetsLoadingOptions,
    //selectedPresetLoadingOptions,
    //setselectedPresetLoadingOptions,
  } = useContext(DashboardContext);
  const { isFitOngoing, isLoopOngoing } = useContext(WebSocketContext);
  const {
    graphs,
    setGraphs,
    chi2Terms,
    setChi2Terms,
    selectedChi2Terms,
    setSelectedChi2Terms,
  } = useContext(GraphContext);
  const { currentUser, isAuthReady } = useContext(AuthContext);
  const { selectedFiles, setSelectedFiles } = useContext(FileHandlingContext);
  const { recordedErrorLog } = useContext(GeneralContext);
  const tutorialProgress = useTutorialProgress();
  const { monitorStateChange, checkApplicationState } = tutorialProgress;
  const { currentTutorial, runningTutorial } = useTutorial();

  // NEW: Check application state when tutorial starts or changes
  useEffect(() => {
    if (currentTutorial && runningTutorial > 0) {
      checkApplicationState({
        uploadedFiles: uploadedFiles || [],
        graphs: graphs || [],
        chi2Terms: chi2Terms || [],
        modelData: modelData || [],
        parametersTabOpen: parametersOpen || false,
        fitTabOpen: fitOpen || false,
        maxIteration: maxIteration || 20,
        iterationRefreshCount: iterationRefreshCount || "",
        maxRunTime: maxRunTime || 100,
        chiVal: chiVal || {
          n: 3,
          val: 0.001,
        },
      });
    }
  }, [currentTutorial, runningTutorial]);
  //const [filesToProcess, setFilesToProcess] = useState([]);
  const [fileProcessingOpen, setFileProcessingOpen] = useState(false);

  /**
   * This function is called when user clicks on the delete icon of a file. It removes the file from the uploadedFiles
   * array and removes it from all graphs.
   * @function handleDeleteClick
   * @param {Event} e - This is the event that is triggered when user clicks on the delete icon.
   * @param {number} index - This is the index of the file in the uploadedFiles array.
   */
  const handleDeleteClick = (e, index) => {
    try {
      e.stopPropagation(); // Prevent triggering the file selection onClick
      // Finds the deleted file and removes it from all graphs, but doesn't remove the graphs themselves
      const fileDeleted = uploadedFiles.find((_, i) => i === index);
      const updatedGraphs = graphs.map((graph) => {
        if (graph.containedFiles.some((id) => id === fileDeleted.ID)) {
          const newFileIdArray = graph.containedFiles.filter(
            (id) => id !== fileDeleted.ID
          );
          const newPlotData = graph.plotData.filter(
            (plot) =>
              !Object.prototype.hasOwnProperty.call(plot, "fileId") ||
              plot.fileId !== fileDeleted.ID
          );

          return {
            ...graph,
            containedFiles: newFileIdArray,
            plotData: newPlotData,
          };
        } else {
          return graph;
        }
      });

      const newSelectedFiles = selectedFiles.filter(
        (selected) => selected.file.ID !== fileDeleted.ID
      );

      if (!isDeepEqual(newSelectedFiles, selectedFiles)) {
        setSelectedFiles(newSelectedFiles);
      }

      setGraphs(updatedGraphs);
      clearChi2AfterFileDelete(fileDeleted.ID);

      // Update DataFile parameters that reference the deleted file
      const updatedModelData = updateDataFileParametersAfterFileDelete(
        fileDeleted.ID,
        modelData
      );
      if (!isDeepEqual(updatedModelData, modelData)) {
        setModelData(updatedModelData);
      }
      setModelData(updatedModelData);

      setUploadedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    } catch (error) {
      recordedErrorLog("Delete click handler failure: ", error);
    }
  };

  /**
   * This function updates DataFile parameters in modelData that reference the deleted file.
   * Sets their value to -1.
   * @function updateDataFileParametersAfterFileDelete
   * @param {number} fileId - This is the ID of the file that was deleted.
   * @param {Array} models - The modelData array to update.
   * @returns {Array} Updated modelData array.
   */
  function updateDataFileParametersAfterFileDelete(fileId, models) {
    const updatedModels = deepCopy(models);
    let deletedFileFound = false;

    function processModel(model) {
      // Remove the deleted file from dataFiles array if it exists
      if (hasProperty(model, "dataFiles") && model.dataFiles.length > 0) {
        const originalLength = model.dataFiles.length;
        model.dataFiles = model.dataFiles.filter((dataFile) => {
          return dataFile.FE_ID !== fileId;
        });
        // If a file was removed from dataFiles, also update parameters that reference it
        if (model.dataFiles.length !== originalLength) {
          deletedFileFound = true;
          
          // Check non-recurring parameters (modelParams) for DataFile parameters that reference the deleted file
          if (
            hasProperty(model, "modelParams") &&
            model.modelParams.length > 0
          ) {
            model.modelParams.forEach((param) => {
              if (
                param.type === "DataFile" &&
                param.value === fileId &&
                param.recuring === 0
              ) {
                param.value = -1;
              }
            });
          }
        }
      }

      // Recursively process submodels
      if (hasProperty(model, "subModels") && model.subModels.length > 0) {
        model.subModels.forEach((subModel) => {
          processModel(subModel);
        });
      }
    }

    updatedModels.forEach((model) => {
      processModel(model);
    });

    if (deletedFileFound) {
      return updatedModels;
    } else {
      return models;
    }
  }

  /**
   * This function clears the chi2 terms that are associated with the deleted file.
   * @function clearChi2AfterFileDelete
   * @param {number} ID - This is the ID of the file that was deleted.
   */
  function clearChi2AfterFileDelete(ID) {
    try {
      const termsCopy = deepCopy(chi2Terms);

      const updatedTerms = [];
      const remainingIDs = [];

      for (let i = 0; i < termsCopy.length; i++) {
        const term = termsCopy[i];
        if (term.fileId !== ID) {
          // Check if term has selectedLoopFiles and remove deleted file from the list
          if (
            hasProperty(term, "selectedLoopFiles") &&
            term.selectedLoopFiles.length > 0
          ) {
            term.selectedLoopFiles = term.selectedLoopFiles.filter(
              (file) => file.ID !== ID
            );
          }
          updatedTerms.push(term);
          remainingIDs.push(term.id);
        }
      }

      const selectedTermsUpdate = selectedChi2Terms.filter((id) =>
        remainingIDs.includes(id)
      );

      setSelectedChi2Terms(selectedTermsUpdate);
      setChi2Terms(updatedTerms);
    } catch (error) {
      recordedErrorLog("Chi2 clearing after file delete failure: ", error);
    }
  }

  /**
   * This function is called when user clicks on the close icon of the file processing window. It clears the files
   * to process and closes the window.
   * @function handleCloseProcessModal
   */
  const handleCloseProcessModal = () => {
    // Monitor state change for tutorial system
    monitorStateChange("data_manager_window_closed", {
      isOpen: false,
      trigger: "user_close",
    });

    setFileProcessingOpen(false);
  };

  /**
   * This function is called when user clicks on the close icon of the file option preset window. It clears the
   * selected preset and closes the window.
   * @function handleClosePresetModal
   */
  // const handleClosePresetModal = () => {
  //   setfilePresetsLoadingOptionsOpen(false);
  // };

  /**
   * This function is called when user clicks on the cancel button of the file processing window. It clears the files
   * to process and closes the window.
   * @function handleCancelProcess
   */
  const handleCancelProcess = () => {
    //  setFilesToProcess([]);
    setFileProcessingOpen(false);
  };

  /**
   * This function is called when user clicks on the solve button of the file processing window. It adds the processed
   * files to the uploadedFiles array and closes the window.
   * @function handleSolveProcess
   * @param {object[]} fileList - This is an array of files that were processed.
   */
  const handleSolveProcess = (fileList) => {
    // Check if any tutorial data files are in the file list
    if (fileList && fileList.length > 0) {
      // NEW: Monitor state change for each file added to uploadedFiles
      fileList.forEach((file) => {
        monitorStateChange("file_added_to_uploaded_files", {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          isTutorialData: file.name.includes("Tutorial"),
          fileListLength: fileList.length,
        });
      });
    }

    setUploadedFiles((prevFiles) => [...prevFiles, ...fileList]);
    //  setFilesToProcess([]);
    setFileProcessingOpen(false);
  };

  /**
   * This function is called when user clicks on the delete icon of a file option preset. It removes the preset from
   * the filePresetsLoadingOptions array.
   * @function handleEntryDelete
   * @param {number} id - This is the ID of the preset that was deleted.
   */
  // const handleEntryDelete = (id) => {
  //   const clearedPresets = filePresetsLoadingOptions.filter((preset) => preset.id !== id);
  //   setfilePresetsLoadingOptions(clearedPresets);
  // };

  /**
   * This function is called when user selects a preset from the file option preset window. It sets the selected preset
   * and closes the window.
   * @function handlePresetSelect
   * @param {number} id - This is the ID of the preset that was selected.
   */
  // const handlePresetSelect = (id) => {
  //   const selectedPresetLoadingOptions = filePresetsLoadingOptions.find((preset) => preset.id === id);
  //   setselectedPresetLoadingOptions(selectedPresetLoadingOptions);
  //   setfilePresetsLoadingOptionsOpen(false);
  // };

  /**
   * This function is called when user clicks on the clear button of the file option preset window. It clears the
   * selected preset and closes the window.
   * @function handlePresetClear
   */
  // const handlePresetClear = () => {
  //   setselectedPresetLoadingOptions(null);
  //   setfilePresetsLoadingOptionsOpen(false);
  // };

  /**
   * This function is called when user clicks to open preset options and this sets up modal location, size and that
   * it needs to be open.
   * @function handlePresetsOpen
   */
  /*const handlePresetsOpen = () => {
    const modalPosition = {
      top:
        presetOptionRef.current != null
          ? presetOptionRef.current.getBoundingClientRect().top
          : 0,
      right: "auto",
      left:
        presetOptionRef.current != null
          ? presetOptionRef.current.getBoundingClientRect().left + 150
          : 0,
    };
    const modalSize = { width: 350, height: 440 };

    const adjusted = adjustModalPositionAndSize(modalPosition, modalSize);

    setPresetModalPlaceAndSize(adjusted);

    setfilePresetsLoadingOptionsOpen(true);
  };*/

  const handleDownload = (file) => {
    try {
      const plotDataForDownload = generatePlotData(
        file.dataPoints,
        file.dataRangeMin,
        file.dataRangeMax,
        "#FFF",
        "Placeholder",
        file.name,
        1,
        null,
        file.edges,
        null,
        null
      );

      const stringifiedData = [];

      for (let i = 0; i < plotDataForDownload.length; i++) {
        const plotPart = plotDataForDownload[i];

        for (let j = 0; j < plotPart.x.length; j++) {
          const pointX = plotPart.x[j];
          const pointY = plotPart.y[j];

          stringifiedData.push(`${pointX}\t${pointY}`);
        }
      }

      const singleString = stringifiedData.join("\n");

      downloadStringTxtFile(singleString, `Speqqle ${file.name}`);
    } catch (error) {
      recordedErrorLog("Error downloading data file: ", error);
    }
  };

  const handleRename = (event, file) => {
    event.preventDefault();
    const newName = prompt("Enter new file name", file.name);
    if (newName !== null && newName !== "") {
      let updatedFiles = deepCopy(uploadedFiles);

      let duplicateName = updatedFiles.some(
        (fileToUpdate) => fileToUpdate.name === newName
      );
      if (duplicateName) {
        alert("File name already exists");
        return;
      }

      updatedFiles = updatedFiles.map((fileToUpdate) => {
        if (fileToUpdate.ID === file.ID) {
          return { ...fileToUpdate, name: newName };
        }
        return fileToUpdate;
      });

      setUploadedFiles(updatedFiles);
    }
  };

  const handleOpenDataManager = () => {
    // NEW: State-based tutorial detection (with safety check)
    if (typeof monitorStateChange === "function") {
      monitorStateChange("data_manager_window_opened", {
        isOpen: true,
        trigger: "user_click",
      });
    } else {
      console.warn(
        "monitorStateChange is not available:",
        typeof monitorStateChange
      );
    }

    setFileProcessingOpen(true);
  };

  const handleFileClick = (e, file, index) => {
    try {
      //const isMac = navigator.userAgent.indexOf("Mac") >= 0;
      /*const multipleSelectionKey = isMac ? e.metaKey : e.ctrlKey;

      if (multipleSelectionKey) {
        // Toggle selection when multipleSelectionKey is pressed (Ctrl or Command key)
        setSelectedFiles((prevSelectedFiles) => {
          if (prevSelectedFiles.includes({ file, index })) {
            // If the file is already selected, remove it from the selection
            return prevSelectedFiles.filter(
              (selectedFile) => selectedFile.file !== file.file
            );
          } else {
            // Otherwise, add the file to the selection
            return [...prevSelectedFiles, { file, index }];
          }
        });
      } else { */
      // If the multipleSelectionKey is not pressed, select only the clicked file
      setSelectedFiles([{ file, index }]);
    } catch (error) {
      recordedErrorLog("File click handler failure: ", error);
    }
  };

  return (
    <div className="fileUpload" id="file-upload-dashboard">
      <div className="uploadButtons" ref={presetOptionRef}>
        {isAuthReady &&
        currentUser.email !== "demo@speqqle.com" &&
        currentUser.email !== "zbjdnmgrklqkaeumso@cwmxc.com" ? (
          <CustomButton
            text="Data Manager"
            extraClassnames={["fileLoaderButton"]}
            handleClick={handleOpenDataManager}
            id="data-manager-button"
          />
        ) : (
          /* <Button
              variant="contained"
              size="small"
              sx={{ m: 1 }}
              onClick={handleOpenDataManager}
              className="fileUploadButton"
              id="data-manager-button"
            >
              Data Manager
            </Button> */
          <div className="demoOnlyWarning" id="restricted-data-demo">
            File loading is for premium users only.
          </div>
        )}
        {/*<SettingsIcon
          id="upload-settings-icon"
          className="icon"
          onClick={() => handlePresetsOpen()}
          sx={{ marginLeft: 'auto' }}
        />*/}
      </div>
      <ul className="file-list">
        {uploadedFiles.map((file, index) => {
          let selected = false;
          for (let i = 0; i < selectedFiles.length; i++) {
            const selectedFile = selectedFiles[i];
            if (isDeepEqual(selectedFile, { file, index })) {
              selected = true;
              break;
            }
          }

          return (
            <li
              id="file-entry"
              key={index}
              onClick={(e) => handleFileClick(e, file, index)}
              className={selected ? "selected" : ""}
            >
              {file.dataPoints.length > 0 ? (
                <HtmlTooltip
                  title={
                    <React.Fragment>
                      <div className="customTooltip">
                        <div className="title">First data point:</div>
                        <div className="data">
                          X:{file.dataPoints[0].x} | Y:{file.dataPoints[0].y}
                        </div>
                      </div>
                    </React.Fragment>
                  }
                >
                  <div
                    className="fileName"
                    id="file-entry-name"
                    onContextMenu={(e) => handleRename(e, file)}
                  >
                    {file.name}
                  </div>
                </HtmlTooltip>
              ) : (
                <div className="fileName" id="file-entry-name">
                  {file.name}
                </div>
              )}
              <DownloadIcon
                className="downloadFileIcon"
                onClick={() => handleDownload(file)}
              />
              <img
                className="deleteFileIcon"
                src={deleteIcon}
                alt={"detele icon"}
                onClick={(e) => {
                  handleDeleteClick(e, index);
                }}
                id="delete-list-entry-icon"
              />
            </li>
          );
        })}
      </ul>
      <Modal
        isOpen={fileProcessingOpen}
        onRequestClose={handleCloseProcessModal}
        shouldCloseOnOverlayClick={true}
        contentLabel="File Processing Modal"
        appElement={presetOptionRef.current}
        id="fileProcessModal"
        style={{
          content: {
            width: "80vw",
            height: "80vh",
            top: `10%`,
            left: "10%",
            right: "auto",
          },
          overlay: {
            zIndex: "8000",
          },
        }}
      >
        <FileProcessingWindow
          cancelProcessing={handleCancelProcess}
          solveProcessing={handleSolveProcess}
          modalId={"fileProcessModal"}
        />
      </Modal>
      {/* Preset Modal */}
      {/*<Modal
        isOpen={filePresetsLoadingOptionsOpen}
        onRequestClose={handleClosePresetModal}
        shouldCloseOnOverlayClick={true}
        contentLabel="File Option Preset Modal"
        appElement={presetOptionRef.current}
        id="fileOptionPresetModal"
        style={{
          content: {
            width: presetModalPlaceAndSize.width,
            height: presetModalPlaceAndSize.height,
            top: presetModalPlaceAndSize.top,
            left: presetModalPlaceAndSize.left,
            right: presetModalPlaceAndSize.right,
          },
          overlay: {
            zIndex: "8000",
          },
        }}
      >
        <div style={{ height: "100%" }}>
          <PresetLoader
            presetList={filePresetsLoadingOptions}
            deleteEntry={handleEntryDelete}
            optionMenu
            setPreset={handlePresetSelect}
            clearPreset={handlePresetClear}
            selectedPresetLoadingOptions={selectedPresetLoadingOptions}
          />
        </div>
      </Modal>*/}
      {isFitOngoing || isLoopOngoing ? <BlockingOverlay /> : <></>}
    </div>
  );
};

export default FileUploader;
